import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  addIntegration,
  connectIntegration,
  disconnectIntegration,
  fetchIntegrations,
  removeIntegration,
  syncIntegration,
} from '../../api/integrations';
import { IntegrationCard } from '../../components/integrations/IntegrationCard';
import { IntegrationSearch } from '../../components/integrations/IntegrationSearch';
import { IntegrationsSummary } from '../../components/integrations/IntegrationsSummary';
import { ConnectApiKeyModal } from '../../components/integrations/ConnectApiKeyModal';
import { ErpExportSettingsModal } from '../../components/integrations/ErpExportSettingsModal';
import { PanelStateMessage } from '../../components/dashboard/PanelStateMessage';
import { INTEGRATION_CATEGORIES } from '../../data/integrationsMock';
import { useAuth } from '../../context/AuthContext';
import { useTenantView } from '../../context/TenantViewContext';

const API_KEY_PROVIDERS = new Set(['klaviyo']);

export function IntegrationsPage() {
  const { isSuperAdmin } = useAuth();
  const { viewingTenant } = useTenantView();
  const accountId = isSuperAdmin && viewingTenant?.id ? viewingTenant.id : null;

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyProvider, setBusyProvider] = useState(null);
  const [banner, setBanner] = useState(null);
  const [modalItem, setModalItem] = useState(null);
  const [erpSettingsItem, setErpSettingsItem] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const connected = params.get('connected');
    const connectError = params.get('connectError');

    if (connected) {
      setBanner({ tone: 'success', message: `${connected} connected.` });
    } else if (connectError) {
      setBanner({ tone: 'error', message: connectError });
    }

    if (connected || connectError) {
      const url = new URL(window.location.href);
      url.search = '';
      window.history.replaceState({}, '', url.toString());
    }
  }, []);

  const load = useCallback(async () => {
    if (isSuperAdmin && !accountId) {
      setItems([]);
      return;
    }

    setLoading(true);
    try {
      const { data } = await fetchIntegrations({ accountId });
      setItems(data.items || []);
      setBanner(null);
    } catch (error) {
      setBanner({
        tone: 'error',
        message: error?.message || 'Unable to load integrations.',
      });
    } finally {
      setLoading(false);
    }
  }, [accountId, isSuperAdmin]);

  useEffect(() => {
    load();
  }, [load]);

  const installedProviders = useMemo(
    () => items.map((item) => item.provider),
    [items]
  );

  const liveByCategory = useMemo(() => {
    return INTEGRATION_CATEGORIES.map((category) => ({
      ...category,
      items: items.filter((item) => item.category === category.id),
    })).filter((category) => category.items.length > 0);
  }, [items]);

  async function runAction(provider, action, successMessage, options = {}) {
    setBusyProvider(provider);
    setBanner(null);
    try {
      await action();
      await load();
      if (successMessage) {
        setBanner({ tone: 'success', message: successMessage });
      }
    } catch (error) {
      setBanner({
        tone: 'error',
        message: error?.message || 'Something went wrong.',
      });
      if (options.rethrow) {
        throw error;
      }
    } finally {
      setBusyProvider(null);
    }
  }

  function handleAdd(catalogItem) {
    return runAction(
      catalogItem.provider,
      () => addIntegration(catalogItem.provider, accountId),
      `${catalogItem.name} added. Connect when you are ready.`
    );
  }

  function handleConnect(item) {
    if (API_KEY_PROVIDERS.has(item.provider)) {
      setModalItem(item);
      return undefined;
    }

    return runAction(
      item.provider,
      () => connectIntegration(item.provider, {}, accountId),
      `${item.name} connected.`
    );
  }

  async function handleModalSubmit(payload) {
    const item = modalItem;
    await runAction(
      item.provider,
      () => connectIntegration(item.provider, payload, accountId),
      `${item.name} connected.`,
      { rethrow: true }
    );
  }

  function handleDisconnect(item) {
    return runAction(
      item.provider,
      () => disconnectIntegration(item.provider, accountId),
      `${item.name} disconnected.`
    );
  }

  function handleSync(item) {
    return runAction(
      item.provider,
      () => syncIntegration(item.provider, accountId),
      `${item.name} sync completed.`
    );
  }

  function handleRemove(item) {
    return runAction(
      item.provider,
      () => removeIntegration(item.provider, accountId),
      `${item.name} removed from your workspace.`
    );
  }

  return (
    <section className="flex w-full min-w-0 flex-col gap-5">
      <p className="max-w-2xl text-xs leading-5 text-slate-600">
        Search and add ERP or marketing tools, then connect with each venue&apos;s own credentials.
      </p>

      {isSuperAdmin && !viewingTenant ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Select a tenant first so integrations are connected to the correct customer account.
        </div>
      ) : null}

      {banner ? (
        <div
          className={
            banner.tone === 'error'
              ? 'rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800'
              : 'rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900'
          }
          role="status"
        >
          {banner.message}
        </div>
      ) : null}

      {isSuperAdmin && !accountId ? (
        <PanelStateMessage
          title="Select a tenant"
          message="Use the Tenants page to open a customer view before managing integrations."
        />
      ) : loading ? (
        <PanelStateMessage
          title="Loading integrations"
          message="Checking connection status for ERP and marketing providers."
        />
      ) : (
        <>
          <IntegrationSearch
            installedProviders={installedProviders}
            busyProvider={busyProvider}
            onAdd={handleAdd}
          />

          <IntegrationsSummary items={items} />

          {items.length === 0 ? (
            <div className="rounded-xl border border-[#C9A227]/35 bg-[#F8F1E3] px-4 py-3">
              <p className="text-sm font-semibold leading-snug text-[#5D2E17]">
                Your workspace is empty
              </p>
              <p className="mt-1 text-[12px] leading-5 text-[#5D2E17]/80">
                Search above for Dynamics, e-conomic, Klaviyo, or HubSpot, click Add, then Connect.
              </p>
            </div>
          ) : (
            liveByCategory.map((category) => (
              <section key={category.id} className="min-w-0 space-y-3">
                <div>
                  <h2 className="font-display text-sm font-bold text-[#201C35]">
                    {category.label}
                  </h2>
                  <p className="mt-0.5 text-xs text-slate-600">{category.description}</p>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                  {category.items.map((item) => (
                    <IntegrationCard
                      key={item.id}
                      item={item}
                      busy={busyProvider === item.provider}
                      onConnect={handleConnect}
                      onDisconnect={handleDisconnect}
                      onSync={handleSync}
                      onRemove={handleRemove}
                      onConfigureExport={
                        item.provider === 'dynamics' ? setErpSettingsItem : undefined
                      }
                    />
                  ))}
                </div>
              </section>
            ))
          )}
        </>
      )}

      {modalItem ? (
        <ConnectApiKeyModal
          item={modalItem}
          onClose={() => setModalItem(null)}
          onSubmit={handleModalSubmit}
        />
      ) : null}

      {erpSettingsItem ? (
        <ErpExportSettingsModal
          item={erpSettingsItem}
          accountId={accountId}
          onClose={() => setErpSettingsItem(null)}
          onSaved={({ message }) => setBanner({ tone: 'success', message })}
        />
      ) : null}
    </section>
  );
}
